import { useEffect } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import Head from 'expo-router/head';
import * as WebBrowser from 'expo-web-browser';
import { Shield } from 'lucide-react-native';
import { useTheme } from '../../src/theme';
import { LoadingSpinner, EmptyState } from '@components/common';
import { Header } from '@components/layout';
import { useUIStore } from '../../src/store/uiStore';
import { planShareUrl } from '@utils/share';

// /plan/<id> is a share link, not a screen of its own — a defense plan only
// has a real UI on the map, docked or drawn. No auth gate: a shared plan is
// world-readable the same way a shared post or structure is (see the
// firestore.rules read branch for defensePlans).
//
// Web: point the map at the plan and land on the map screen, same as the
// structure/post focus channels (uiStore.pendingMapFocus), just entered
// through a URL instead of an in-app tap. Native: drawing and viewing a plan
// both need the map's docked panel and Leaflet layer, neither of which
// exists on native (B9 — desktop web only), so this is a dead end that opens
// the web page instead of pretending to render one.
//
// Gets back to the tabs screen with router.back(), not router.replace (or
// router.navigate, which behaves the same way here). `(tabs)` is
// app/_layout.tsx's initialRouteName, so on a cold direct load of this URL
// expo-router already mounts it in the background before pushing this
// screen on top of it, confirmed by instrumenting Map.web.tsx's init
// effect, which fired once, un-navigated, before any of this file's code
// ran. Both `replace` and `navigate` treat that background instance as no
// longer part of the active history once this screen is the current route,
// so targeting '(tabs)' by name mounts a SECOND instance rather than
// returning to the first: two Map instances alive at once, each
// independently drawing the plan's features (12 paths for a 6-feature plan
// instead of 6). `back()` pops the actual stack entry instead of navigating
// by name, landing back on that one pre-existing instance.
//
// Not guarded with router.canGoBack(): that check runs synchronously in
// this same early effect, before the navigator reports itself ready, and
// reads false here even though the queued GO_BACK action it would have
// gated succeeds once the navigator settles a tick later (verified by
// comparing the guarded and unguarded builds against the seeded demo
// plan). The initialRouteName above means a '(tabs)' entry always exists
// under this screen, so back() has somewhere to land.
export default function PlanShareScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const closePlanPanel = useUIStore((s) => s.closePlanPanel);
  const setViewedPlanId = useUIStore((s) => s.setViewedPlanId);
  const requestMapPlanFocus = useUIStore((s) => s.requestMapPlanFocus);
  const markArrivedViaPlanLink = useUIStore((s) => s.markArrivedViaPlanLink);

  useEffect(() => {
    if (Platform.OS !== 'web' || !id) return;
    // A visitor following someone's plan link is here for the plan, not the
    // "Welcome to REDI" cards — skip that overlay for this one visit only
    // (see uiStore.arrivedViaPlanLink and selectMapPathsOverlayActive).
    markArrivedViaPlanLink();
    closePlanPanel();
    setViewedPlanId(id);
    requestMapPlanFocus(id);
    router.back();
  }, [id, closePlanPanel, setViewedPlanId, requestMapPlanFocus, markArrivedViaPlanLink]);

  const handleOpenOnWeb = () => {
    if (id) void WebBrowser.openBrowserAsync(planShareUrl(id));
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>Defense plan - Redi</title>
      </Head>
      {Platform.OS === 'web' ? (
        <LoadingSpinner fullScreen />
      ) : (
        <>
          <Header title="Defense plan" showBack />
          <View style={styles.emptyWrap}>
            <EmptyState
              icon={Shield}
              title="Defense plans are on the web for now"
              message="Open this plan in a browser to see it drawn on the map."
              actionLabel="Open in browser"
              onAction={handleOpenOnWeb}
            />
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  emptyWrap: {
    flex: 1,
    justifyContent: 'center',
  },
});
